import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

const BGM_STORAGE_KEY = 'bbt-bgm-enabled';
const DEFAULT_BGM_URL = 'https://hmseykssvtyrihuxymeo.supabase.co/storage/v1/object/public/bbt/bgm/bubble_bgm.m4a';
const BGM_DEFAULT_VOLUME = 0.4;
const BGM_FADE_DURATION_MS = 3000;
const BGM_FADE_WINDOW_SECONDS = 3;
const BGM_LOOP_START_OFFSET_SECONDS = 0.08;
const BGM_LOOP_RESTART_THRESHOLD_SECONDS = 0.12;

const clampVolume = (value: number) => Math.min(1, Math.max(0, value));

type BgmContextValue = {
  isBgmEnabled: boolean;
  setBgmEnabled: (enabled: boolean) => void;
  toggleBgm: () => void;
  setBgmSource: (sourceUrl?: string | null) => void;
};

const BgmContext = createContext<BgmContextValue | null>(null);

export const BgmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isBgmEnabled, setIsBgmEnabled] = useState<boolean>(() => {
    const stored = localStorage.getItem(BGM_STORAGE_KEY);
    return stored === null ? true : stored === 'true';
  });
  const [bgmSource, setBgmSourceState] = useState<string>(DEFAULT_BGM_URL);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fadeRafRef = useRef<number | null>(null);
  const isLoopFadeOutTriggeredRef = useRef(false);
  const isLoopRestartingRef = useRef(false);
  const shouldResumeAfterForegroundRef = useRef(false);

  const cancelFade = useCallback(() => {
    if (fadeRafRef.current !== null) {
      window.cancelAnimationFrame(fadeRafRef.current);
      fadeRafRef.current = null;
    }
  }, []);

  const stopBgmImmediately = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    cancelFade();
    audio.pause();
    audio.currentTime = 0;
    audio.volume = BGM_DEFAULT_VOLUME;
    isLoopFadeOutTriggeredRef.current = false;
    isLoopRestartingRef.current = false;
  }, [cancelFade]);

  const fadeVolume = useCallback((audio: HTMLAudioElement, targetVolume: number, durationMs: number, onComplete?: () => void) => {
    cancelFade();

    const startVolume = clampVolume(audio.volume);
    const normalizedTargetVolume = clampVolume(targetVolume);
    const startedAt = performance.now();

    const step = (now: number) => {
      const progress = Math.min((now - startedAt) / durationMs, 1);
      const nextVolume = startVolume + (normalizedTargetVolume - startVolume) * progress;
      audio.volume = clampVolume(nextVolume);

      if (progress < 1) {
        fadeRafRef.current = window.requestAnimationFrame(step);
        return;
      }

      fadeRafRef.current = null;
      onComplete?.();
    };

    fadeRafRef.current = window.requestAnimationFrame(step);
  }, [cancelFade]);

  const ensureAudio = useCallback(() => {
    if (!audioRef.current) {
      const audio = new Audio(bgmSource);
      audio.loop = false;
      audio.preload = 'auto';
      audio.playsInline = true;
      audio.volume = BGM_DEFAULT_VOLUME;
      audioRef.current = audio;
    }
    return audioRef.current;
  }, [bgmSource]);

  const startBgm = useCallback(async () => {
    if (!isBgmEnabled) return;
    const audio = ensureAudio();

    if (!audio.paused) {
      return;
    }

    try {
      let shouldFadeIn = false;

      if (audio.currentTime === 0) {
        audio.currentTime = BGM_LOOP_START_OFFSET_SECONDS;
        audio.volume = 0;
        shouldFadeIn = true;
      }

      await audio.play();

      if (shouldFadeIn || audio.volume === 0 || audio.currentTime <= 0.05) {
        fadeVolume(audio, BGM_DEFAULT_VOLUME, BGM_FADE_DURATION_MS);
      }
    } catch (error) {
      console.warn('⚠️ [BGM] 背景音樂播放失敗，等待使用者互動後重試', error);
    }
  }, [ensureAudio, fadeVolume, isBgmEnabled]);

  useEffect(() => {
    const audio = ensureAudio();

    const handleTimeUpdate = () => {
      if (!isBgmEnabled || !Number.isFinite(audio.duration) || audio.duration <= 0) return;

      const remain = audio.duration - audio.currentTime;
      if (remain <= BGM_FADE_WINDOW_SECONDS && !isLoopFadeOutTriggeredRef.current) {
        isLoopFadeOutTriggeredRef.current = true;
        fadeVolume(audio, 0, BGM_FADE_DURATION_MS);
      }

      if (
        remain <= BGM_LOOP_RESTART_THRESHOLD_SECONDS
        && !isLoopRestartingRef.current
      ) {
        isLoopRestartingRef.current = true;

        try {
          audio.currentTime = BGM_LOOP_START_OFFSET_SECONDS;
          audio.volume = 0;
          isLoopFadeOutTriggeredRef.current = false;
          fadeVolume(audio, BGM_DEFAULT_VOLUME, BGM_FADE_DURATION_MS);
        } finally {
          isLoopRestartingRef.current = false;
        }
      }
    };

    const handleEnded = async () => {
      if (!isBgmEnabled) return;

      try {
        isLoopFadeOutTriggeredRef.current = false;
        audio.currentTime = BGM_LOOP_START_OFFSET_SECONDS;
        audio.volume = 0;
        await audio.play();
        fadeVolume(audio, BGM_DEFAULT_VOLUME, BGM_FADE_DURATION_MS);
      } catch (error) {
        console.warn('⚠️ [BGM] 背景音樂重播失敗，等待使用者互動後重試', error);
      }
    };

    const handlePlaying = () => {
      if (audio.currentTime > BGM_FADE_WINDOW_SECONDS) {
        isLoopFadeOutTriggeredRef.current = false;
      }

      if (audio.currentTime > BGM_LOOP_START_OFFSET_SECONDS + 0.3) {
        isLoopRestartingRef.current = false;
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    const handleEndedEvent = () => {
      void handleEnded();
    };

    audio.addEventListener('ended', handleEndedEvent);
    audio.addEventListener('playing', handlePlaying);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEndedEvent);
      audio.removeEventListener('playing', handlePlaying);
    };
  }, [ensureAudio, fadeVolume, isBgmEnabled]);

  useEffect(() => {
    localStorage.setItem(BGM_STORAGE_KEY, String(isBgmEnabled));

    ensureAudio();
    if (isBgmEnabled) {
      void startBgm();
    } else {
      stopBgmImmediately();
    }
  }, [ensureAudio, isBgmEnabled, startBgm, stopBgmImmediately]);

  useEffect(() => {
    const audio = ensureAudio();

    if (audio.src !== bgmSource) {
      cancelFade();
      const applySource = async () => {
        audio.pause();
        audio.src = bgmSource;
        audio.currentTime = 0;
        audio.volume = BGM_DEFAULT_VOLUME;
        isLoopFadeOutTriggeredRef.current = false;
        isLoopRestartingRef.current = false;

        if (!isBgmEnabled) return;

        audio.currentTime = BGM_LOOP_START_OFFSET_SECONDS;
        audio.volume = 0;

        try {
          await audio.play();
          fadeVolume(audio, BGM_DEFAULT_VOLUME, BGM_FADE_DURATION_MS);
        } catch (error) {
          console.warn('⚠️ [BGM] 切換背景音樂播放失敗，等待使用者互動後重試', error);
        }
      };

      void applySource();
    }
  }, [bgmSource, cancelFade, ensureAudio, fadeVolume, isBgmEnabled]);

  useEffect(() => {
    if (!isBgmEnabled) return;

    const unlockAndPlay = () => {
      void startBgm();
    };

    window.addEventListener('pointerdown', unlockAndPlay, { passive: true });
    window.addEventListener('keydown', unlockAndPlay);
    window.addEventListener('touchstart', unlockAndPlay, { passive: true });

    return () => {
      window.removeEventListener('pointerdown', unlockAndPlay);
      window.removeEventListener('keydown', unlockAndPlay);
      window.removeEventListener('touchstart', unlockAndPlay);
    };
  }, [isBgmEnabled, startBgm]);

  useEffect(() => {
    const handleAppBackgroundState = () => {
      const audio = audioRef.current;

      if (document.hidden) {
        shouldResumeAfterForegroundRef.current = Boolean(
          isBgmEnabled
          && audio
          && !audio.paused
        );
        stopBgmImmediately();
        return;
      }

      if (isBgmEnabled && shouldResumeAfterForegroundRef.current) {
        shouldResumeAfterForegroundRef.current = false;
        void startBgm();
      }
    };

    const handlePageHide = () => {
      const audio = audioRef.current;
      shouldResumeAfterForegroundRef.current = Boolean(
        isBgmEnabled
        && audio
        && !audio.paused
      );
      stopBgmImmediately();
    };

    document.addEventListener('visibilitychange', handleAppBackgroundState);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      document.removeEventListener('visibilitychange', handleAppBackgroundState);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [isBgmEnabled, startBgm, stopBgmImmediately]);

  const setBgmEnabled = useCallback((enabled: boolean) => {
    setIsBgmEnabled(enabled);
  }, []);

  const toggleBgm = useCallback(() => {
    setIsBgmEnabled(prev => !prev);
  }, []);

  const setBgmSource = useCallback((sourceUrl?: string | null) => {
    const normalizedUrl = sourceUrl?.trim();
    setBgmSourceState(normalizedUrl || DEFAULT_BGM_URL);
  }, []);

  useEffect(() => () => {
    stopBgmImmediately();
  }, [stopBgmImmediately]);

  return (
    <BgmContext.Provider value={{ isBgmEnabled, setBgmEnabled, toggleBgm, setBgmSource }}>
      {children}
    </BgmContext.Provider>
  );
};

export const useBgm = () => {
  const context = useContext(BgmContext);
  if (!context) {
    throw new Error('useBgm 必須在 BgmProvider 內使用');
  }
  return context;
};
